import React, { useState, useEffect } from "react";
import {
  Container,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Box,
  Tab,
  Tabs,
  ThemeProvider,
  createTheme,
  Button,
  Tooltip,
} from "@mui/material";
import WardrobeIcon from "@mui/icons-material/Checkroom";
import BuildIcon from "@mui/icons-material/Build";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { ClothingItemUpload } from "./components/ClothingItemUpload";
import { WardrobeList } from "./components/WardrobeList";
import { OutfitBuilder } from "./components/OutfitBuilder";
import { Login } from "./components/Login";
import { Register } from "./components/Register";
import { HomePage } from "./components/HomePage";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ClothingItem } from "./types/wardrobe";
import { api } from "./services/api";
import { useResponsive } from "./hooks/useResponsive";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const theme = createTheme({
  typography: {
    fontFamily: [
      "Inter",
      "SF Pro Display",
      "-apple-system",
      "BlinkMacSystemFont",
      "Segoe UI",
      "Roboto",
      "Helvetica Neue",
      "Arial",
      "sans-serif",
    ].join(","),
    h1: {
      fontFamily: [
        "Inter",
        "SF Pro Display",
        "-apple-system",
        "BlinkMacSystemFont",
        "Segoe UI",
        "Roboto",
        "Helvetica Neue",
        "Arial",
        "sans-serif",
      ].join(","),
      fontWeight: 700,
      letterSpacing: "-0.02em",
    },
    h2: {
      fontFamily: [
        "Inter",
        "SF Pro Display",
        "-apple-system",
        "BlinkMacSystemFont",
        "Segoe UI",
        "Roboto",
        "Helvetica Neue",
        "Arial",
        "sans-serif",
      ].join(","),
      fontWeight: 600,
      letterSpacing: "-0.01em",
    },
    h3: {
      fontFamily: [
        "Inter",
        "SF Pro Display",
        "-apple-system",
        "BlinkMacSystemFont",
        "Segoe UI",
        "Roboto",
        "Helvetica Neue",
        "Arial",
        "sans-serif",
      ].join(","),
      fontWeight: 600,
      letterSpacing: "-0.01em",
    },
    h4: {
      fontFamily: [
        "Inter",
        "SF Pro Display",
        "-apple-system",
        "BlinkMacSystemFont",
        "Segoe UI",
        "Roboto",
        "Helvetica Neue",
        "Arial",
        "sans-serif",
      ].join(","),
      fontWeight: 600,
      letterSpacing: "-0.01em",
    },
    h5: {
      fontFamily: [
        "Inter",
        "SF Pro Display",
        "-apple-system",
        "BlinkMacSystemFont",
        "Segoe UI",
        "Roboto",
        "Helvetica Neue",
        "Arial",
        "sans-serif",
      ].join(","),
      fontWeight: 600,
      letterSpacing: "-0.01em",
    },
    h6: {
      fontFamily: [
        "Inter",
        "SF Pro Display",
        "-apple-system",
        "BlinkMacSystemFont",
        "Segoe UI",
        "Roboto",
        "Helvetica Neue",
        "Arial",
        "sans-serif",
      ].join(","),
      fontWeight: 600,
      letterSpacing: "-0.01em",
    },
    body1: {
      fontFamily: [
        "Inter",
        "SF Pro Display",
        "-apple-system",
        "BlinkMacSystemFont",
        "Segoe UI",
        "Roboto",
        "Helvetica Neue",
        "Arial",
        "sans-serif",
      ].join(","),
      fontWeight: 400,
      letterSpacing: "0.01em",
    },
    body2: {
      fontFamily: [
        "Inter",
        "SF Pro Display",
        "-apple-system",
        "BlinkMacSystemFont",
        "Segoe UI",
        "Roboto",
        "Helvetica Neue",
        "Arial",
        "sans-serif",
      ].join(","),
      fontWeight: 400,
      letterSpacing: "0.01em",
    },
    button: {
      fontFamily: [
        "Inter",
        "SF Pro Display",
        "-apple-system",
        "BlinkMacSystemFont",
        "Segoe UI",
        "Roboto",
        "Helvetica Neue",
        "Arial",
        "sans-serif",
      ].join(","),
      fontWeight: 500,
      letterSpacing: "0.02em",
      textTransform: "none",
    },
    caption: {
      fontFamily: [
        "Inter",
        "SF Pro Display",
        "-apple-system",
        "BlinkMacSystemFont",
        "Segoe UI",
        "Roboto",
        "Helvetica Neue",
        "Arial",
        "sans-serif",
      ].join(","),
      fontWeight: 400,
      letterSpacing: "0.02em",
    },
    overline: {
      fontFamily: [
        "Inter",
        "SF Pro Display",
        "-apple-system",
        "BlinkMacSystemFont",
        "Segoe UI",
        "Roboto",
        "Helvetica Neue",
        "Arial",
        "sans-serif",
      ].join(","),
      fontWeight: 500,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
    },
  },
  palette: {
    primary: {
      main: "#1A1A1A", // Jet Black
      light: "#404040",
      dark: "#000000",
      contrastText: "#FFFCF7", // Ivory White
    },
    secondary: {
      main: "#C49A88", // Clay Rose
      light: "#D4B4A8",
      dark: "#B08570",
      contrastText: "#1A1A1A", // Jet Black
    },
    background: {
      default: "#F4EDE4", // Sand
      paper: "#FFFCF7", // Ivory White
    },
    text: {
      primary: "#1A1A1A", // Jet Black
      secondary: "#6B7B8C", // Slate Blue
    },
    action: {
      hover: "#FFFCF7", // Ivory White
      selected: "#AABBA5", // Sage Green
      active: "#C49A88", // Clay Rose
    },
    divider: "#C49A88", // Clay Rose
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#1A1A1A", // Jet Black
          color: "#FFFCF7", // Ivory White
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: "#6B7B8C", // Slate Blue
          fontFamily:
            "Inter, SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif",
          fontWeight: 500,
          letterSpacing: "0.02em",
          "&.Mui-selected": {
            color: "#1A1A1A", // Jet Black
            fontWeight: 600,
          },
          "&:hover": {
            color: "#C49A88", // Clay Rose
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: "#C49A88", // Clay Rose
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          letterSpacing: "0.02em",
          "&:hover": {
            backgroundColor: "#AABBA5", // Sage Green
          },
        },
        contained: {
          backgroundColor: "#1A1A1A", // Jet Black
          color: "#FFFCF7", // Ivory White
          "&:hover": {
            backgroundColor: "#404040", // Jet Black light
          },
        },
        outlined: {
          borderColor: "#C49A88", // Clay Rose
          color: "#1A1A1A", // Jet Black
          "&:hover": {
            backgroundColor: "#F4EDE4", // Sand
            borderColor: "#B08570", // Clay Rose dark
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFFCF7", // Ivory White
          border: "1px solid #C49A88", // Clay Rose
          borderRadius: 12,
          "&:hover": {
            boxShadow: "0 4px 12px rgba(26, 26, 26, 0.1)",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: "#AABBA5", // Sage Green
          color: "#1A1A1A", // Jet Black
          fontFamily:
            "Inter, SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif",
          fontWeight: 500,
          letterSpacing: "0.01em",
          "&.MuiChip-colorPrimary": {
            backgroundColor: "#1A1A1A", // Jet Black
            color: "#FFFCF7", // Ivory White
          },
          "&.MuiChip-colorSecondary": {
            backgroundColor: "#C49A88", // Clay Rose
            color: "#1A1A1A", // Jet Black
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily:
            "Inter, SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif",
        },
      },
    },
  },
});

const AppContent: React.FC = () => {
  const { isAuthenticated, logout, user, isDemoMode, enterDemoMode } =
    useAuth();
  const { isMobile, isTablet } = useResponsive();
  const [tabValue, setTabValue] = useState(0);

  // Debug tab value changes
  useEffect(() => {
    console.log("Tab value changed to:", tabValue);
  }, [tabValue]);
  const [authMode, setAuthMode] = useState<"home" | "login" | "register">(
    "home"
  );
  const [wardrobeItems, setWardrobeItems] = useState<ClothingItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [triggerUpload, setTriggerUpload] = useState(false);

  useEffect(() => {
    const fetchWardrobeItems = async () => {
      if (isAuthenticated) {
        try {
          setIsLoading(true);
          const items = await api.getWardrobe();
          setWardrobeItems(items);
        } catch (error) {
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchWardrobeItems();
  }, [isAuthenticated, isDemoMode]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    console.log(
      "Tab change:",
      newValue,
      "isDemoMode:",
      isDemoMode,
      "isMobile:",
      isMobile,
      "isTablet:",
      isTablet
    );

    // For mobile/tablet, if clicking upload tab, trigger upload modal instead of changing tab
    if ((isMobile || isTablet) && newValue === 2) {
      // Don't trigger upload in demo mode
      if (isDemoMode) {
        console.log("Blocked upload in demo mode (mobile/tablet)");
        return;
      }
      console.log("Triggering upload modal (mobile/tablet)");
      setTriggerUpload(true);
      // Reset trigger after a brief moment
      setTimeout(() => setTriggerUpload(false), 100);
      return;
    }
    // For desktop, if clicking upload tab, trigger upload modal instead of changing tab
    if (!isMobile && !isTablet && newValue === 2) {
      // Don't trigger upload in demo mode
      if (isDemoMode) {
        console.log("Blocked upload in demo mode (desktop)");
        return;
      }
      console.log("Triggering upload modal (desktop)");
      setTriggerUpload(true);
      // Reset trigger after a brief moment
      setTimeout(() => setTriggerUpload(false), 100);
      return;
    }
    setTabValue(newValue);
  };

  const handleItemAdded = (items: ClothingItem | ClothingItem[]) => {
    if (Array.isArray(items)) {
      setWardrobeItems((prev) => [...prev, ...items]);
    } else {
      setWardrobeItems((prev) => [...prev, items]);
    }
  };

  const handleItemDeleted = (itemId: string) => {
    setWardrobeItems((prev) => prev.filter((item) => item._id !== itemId));
  };

  const handleItemUpdate = (updatedItems: ClothingItem[]) => {
    setWardrobeItems(updatedItems);
  };

  if (!isAuthenticated) {
    if (authMode === "home") {
      return (
        <HomePage
          onLoginClick={() => setAuthMode("login")}
          onRegisterClick={() => setAuthMode("register")}
          onDemoClick={enterDemoMode}
          isMobile={isMobile}
          isTablet={isTablet}
        />
      );
    } else if (authMode === "login") {
      return (
        <Login
          onSwitchToRegister={() => setAuthMode("register")}
          onBackToHome={() => setAuthMode("home")}
        />
      );
    } else {
      return (
        <Box>
          <Register onBackToHome={() => setAuthMode("home")} />
          <Box sx={{ textAlign: "center", mt: 2 }}>
            <Typography variant="body2">
              Already have an account?{" "}
              <Button color="primary" onClick={() => setAuthMode("login")}>
                Login here
              </Button>
            </Typography>
          </Box>
        </Box>
      );
    }
  }

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Box
            component="img"
            src="/tanhanger.png"
            alt="NO STYLIST Logo"
            sx={{
              width: isMobile ? 32 : 40,
              height: isMobile ? 32 : 40,
              mr: 2,
              objectFit: "contain",
            }}
          />
          <Typography
            variant={isMobile ? "subtitle1" : "h6"}
            component="div"
            sx={{ flexGrow: 1 }}
          >
            NO STYLIST
          </Typography>
          {!isMobile && (
            <Typography variant="body1" sx={{ mr: 2 }}>
              {isDemoMode ? "DEMO" : `Welcome, ${user?.name}`}
            </Typography>
          )}
          <Button
            color="inherit"
            onClick={logout}
            size={isMobile ? "small" : "medium"}
            sx={{
              minWidth: "auto",
              px: isMobile ? 1 : 2,
              py: 1,
              fontSize: isMobile ? "0.75rem" : "0.875rem",
              fontWeight: 500,
              textTransform: "none",
              border: "1px solid rgba(255, 252, 247, 0.3)",
              borderRadius: 1,
              "&:hover": {
                backgroundColor: "rgba(255, 252, 247, 0.1)",
                borderColor: "rgba(255, 252, 247, 0.5)",
              },
            }}
          >
            {isDemoMode ? "End Demo" : "Log out"}
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ px: isMobile ? 1 : 3 }}>
        {/* Desktop Tabs */}
        {!isMobile && !isTablet && (
          <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 3 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="wardrobe tabs"
            >
              <Tab
                label="My Wardrobe"
                id="simple-tab-0"
                aria-controls="simple-tabpanel-0"
              />
              <Tab
                label="Outfit Builder"
                id="simple-tab-1"
                aria-controls="simple-tabpanel-1"
              />
              <Tooltip
                title={isDemoMode ? "Disabled in demo mode" : ""}
                arrow
                placement="top"
              >
                <span>
                  <Tab
                    label="Upload Items"
                    id="simple-tab-2"
                    aria-controls="simple-tabpanel-2"
                    disabled={isDemoMode}
                    onClick={(e) => {
                      console.log("Upload tab clicked directly!", e);
                      if (!isDemoMode) {
                        console.log(
                          "Triggering upload modal from direct click"
                        );
                        setTriggerUpload(true);
                        setTimeout(() => setTriggerUpload(false), 100);
                      }
                    }}
                  />
                </span>
              </Tooltip>
            </Tabs>
          </Box>
        )}

        {/* Mobile Tab Navigation */}
        {(isMobile || isTablet) && (
          <Box
            sx={{
              mt: 2,
              mb: 2,
              backgroundColor: "#FFFCF7",
              borderRadius: 2,
              border: "1px solid #C49A88",
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="fullWidth"
              aria-label="mobile wardrobe tabs"
              sx={{
                display: "flex",
                "& .MuiTab-root": {
                  color: "#6B7B8C",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  py: 1.5,
                  flex: 1,
                  minWidth: 0,
                  "&.Mui-selected": {
                    color: "#1A1A1A",
                  },
                  "&:hover": {
                    color: "#C49A88",
                  },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#C49A88",
                  height: 3,
                },
              }}
            >
              <Tab
                label="My Wardrobe"
                id="mobile-tab-0"
                aria-controls="mobile-tabpanel-0"
                icon={<WardrobeIcon />}
                iconPosition="start"
              />
              <Tab
                label="Outfit Builder"
                id="mobile-tab-1"
                aria-controls="mobile-tabpanel-1"
                icon={<BuildIcon />}
                iconPosition="start"
              />
              <Tab
                label="Upload Items"
                id="mobile-tab-2"
                aria-controls="mobile-tabpanel-2"
                icon={<CloudUploadIcon />}
                iconPosition="start"
                disabled={isDemoMode}
                onClick={(e) => {
                  console.log("Mobile upload tab clicked directly!", e);
                  if (!isDemoMode) {
                    console.log(
                      "Triggering upload modal from direct click (mobile)"
                    );
                    setTriggerUpload(true);
                    setTimeout(() => setTriggerUpload(false), 100);
                  }
                }}
                title={isDemoMode ? "Disabled in demo mode" : ""}
              />
            </Tabs>
          </Box>
        )}

        <TabPanel value={tabValue} index={0}>
          <WardrobeList
            items={wardrobeItems}
            onItemDeleted={handleItemDeleted}
            onItemUpdate={handleItemUpdate}
            isDemoMode={isDemoMode}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <OutfitBuilder
            items={wardrobeItems}
            onItemUpdate={handleItemUpdate}
          />
        </TabPanel>
        {/* Upload Tab Panel - Desktop Only */}
        {!isMobile && !isTablet && (
          <TabPanel value={tabValue} index={2}>
            <Box
              sx={{
                p: 3,
                backgroundColor: "#F4EDE4",
                minHeight: "100vh",
              }}
            >
              <ClothingItemUpload
                onUploadComplete={(newItems: ClothingItem | ClothingItem[]) => {
                  const itemsToAdd = Array.isArray(newItems)
                    ? newItems
                    : [newItems];
                  setWardrobeItems([...wardrobeItems, ...itemsToAdd]);
                }}
                triggerOpen={triggerUpload}
              />
            </Box>
          </TabPanel>
        )}
      </Container>

      {/* Hidden Upload Component for Modal Trigger */}
      <ClothingItemUpload
        onUploadComplete={(newItems: ClothingItem | ClothingItem[]) => {
          const itemsToAdd = Array.isArray(newItems) ? newItems : [newItems];
          setWardrobeItems([...wardrobeItems, ...itemsToAdd]);
        }}
        triggerOpen={triggerUpload}
        hidden={true}
      />
    </>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
